import { ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert, Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row,
} from "reactstrap";
import { signUp, confirmSignUp, resendSignUpCode, signIn, signOut } from "aws-amplify/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFileInvoice, faUsers } from "@fortawesome/free-solid-svg-icons";
import styles from "./style.module.scss";
import LoadingScreen from "@components/content/LoadingScreen";
import OnvoSubscriptionPayment from "@components/content/OnvoSubscriptionPayment";
import { showAlert } from "@utils/Alert";
import { getRecaptchaToken } from "@utils/recaptcha";
import { TERMS_VERSION } from "@/security/terms.catalog";
import { fetchSuscription } from "@/api/SuscriptionsApi";
import { TenantApi, TenantDataType } from "@/api/TenantApi";
import { SuscriptionTypeApi, SuscriptionTypeDataType } from "@/api/SuscriptionTypeApi";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { startPlanUpgrade } from "@/api/OnvoPayApi";
import { ONVO_TENANT_PAYMENT_TYPE_CODE } from "@utils/Constants";
import { formatMoneyValue } from "@utils/Currency";
import { PLAN_FEATURE_CATALOG, isFeatureAllowed, parsePlanFeatures } from "@/security/features.catalog";
import { DOCUMENT_LIMIT_CATALOG, formatLimitLabel, getDocumentLimit, parseDocumentLimits } from "@/security/documentLimits.catalog";

type WizardStep = "account" | "verify" | "plan" | "payment";

// Al terminar navegamos a "/" (gate de Login): detecta la sesión ya iniciada, hidrata el
// store de Redux (suscripción, tenant, features, límites, tipo de cambio) y redirige al dashboard.
const HOME_URL = "/";
const MAX_CARD_FEATURES = 5;

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>("account");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paso 1 — cuenta
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefonoCd, setTelefonoCd] = useState("506");
  const [telefono, setTelefono] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Paso 2 — verificación
  const [code, setCode] = useState("");

  // Paso 3/4 — plan y pago
  const [plans, setPlans] = useState<SuscriptionTypeDataType[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDataType[]>([]);
  const [tenant, setTenant] = useState<TenantDataType | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SuscriptionTypeDataType | null>(null);
  const [onvoSession, setOnvoSession] = useState<{ onvoCustomerId: string; onvoSubscriptionId: string } | null>(null);

  const validateAccount = (): string | null => {
    if (!nombre.trim()) return "Ingrese su nombre o el de su negocio";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return "Ingrese un correo válido";
    if (!telefono.trim()) return "Ingrese su número de teléfono";
    if (password.length < 8) return "La contraseña debe tener al menos 8 caracteres";
    if (password !== password2) return "Las contraseñas no coinciden";
    if (!acceptedTerms) return "Debe aceptar los términos de uso para continuar";
    return null;
  };

  const submitAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateAccount();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // Garantiza que no haya una sesión activa previa antes de registrar.
      await signOut().catch(() => undefined);
      const recaptchaToken = await getRecaptchaToken("register");
      // El teléfono no se guarda como atributo de Cognito (puede no estar en los atributos
      // escribibles del app client): se envía en clientMetadata al confirmar y se persiste en
      // Suscription/Tenant desde el trigger post-confirmation.
      await signUp({
        username: email.trim(),
        password,
        options: {
          userAttributes: { name: nombre.trim() },
          clientMetadata: recaptchaToken ? { recaptchaToken } : {},
        },
      });
      setStep("verify");
    } catch (err: any) {
      if (err?.name === "UsernameExistsException") {
        setError("Ya existe una cuenta con este correo. Inicie sesión.");
      } else {
        setError(err?.message || "No fue posible iniciar el registro");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAccountAndPlans = async () => {
    const profile = await fetchSuscription();
    const tenantData = await TenantApi.fetchTenantBySuscriptionId(`${profile.id}`);
    setTenant(tenantData);
    const [{ items: planList }, { items: currencyList }] = await Promise.all([
      SuscriptionTypeApi.fetchAllByListNoTenant({}, undefined, 100),
      CurrencyApi.fetchAll(),
    ]);
    setCurrencies(currencyList);
    setPlans([...planList].sort((a, b) => Number(a.monto || 0) - Number(b.monto || 0)));
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError("Ingrese el código que enviamos a su correo");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await confirmSignUp({
        username: email.trim(),
        confirmationCode: code.trim(),
        options: {
          clientMetadata: { phone: telefono, phoneCd: telefonoCd, termsVersion: TERMS_VERSION },
        },
      });
      // El trigger post-confirmation provisiona la cuenta; luego iniciamos sesión.
      await signIn({ username: email.trim(), password });
      await loadAccountAndPlans();
      setStep("plan");
    } catch (err: any) {
      setError(err?.message || "El código no es válido o expiró");
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      await resendSignUpCode({ username: email.trim() });
      showAlert("success", "Le enviamos un nuevo código a su correo");
    } catch (err: any) {
      showAlert("danger", err?.message || "No fue posible reenviar el código");
    }
  };

  const getCurrencyNumericId = (currencyId?: string | null): number => {
    const found = currencies.find(c => c.id === currencyId);
    return Number(found?.currency_id || 1);
  };

  const choosePlan = async (plan: SuscriptionTypeDataType) => {
    if (!tenant?.id) return;
    setError(null);
    setSelectedPlan(plan);
    const isFree = !plan.onvoPriceId;
    setLoading(true);
    try {
      if (isFree) {
        await TenantApi.update(`${tenant.id}`, { suscriptionTypeId: plan.id, status: "active" });
        finishRegistration();
        return;
      }
      const session = await startPlanUpgrade(tenant, plan);
      setOnvoSession(session);
      setStep("payment");
    } catch (err: any) {
      setError(err?.message || "No fue posible iniciar el proceso de pago");
      setSelectedPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const onPaymentSuccess = async (data: any) => {
    if (!tenant?.id || !selectedPlan) return;
    setLoading(true);
    try {
      await TenantApi.update(`${tenant.id}`, {
        suscriptionTypeId: selectedPlan.id,
        onvoPaymentMethodId: data.paymentMethodId,
        onvoCustomerId: onvoSession?.onvoCustomerId,
        onvoSubscriptionId: onvoSession?.onvoSubscriptionId,
        tipoPago: ONVO_TENANT_PAYMENT_TYPE_CODE,
        status: "active",
      });
      showAlert("success", "¡Suscripción activada! Bienvenido a Easypos");
      finishRegistration();
    } catch (err: any) {
      setError(err?.message || "El pago se procesó pero no fue posible activar el plan. Contacte a soporte.");
    } finally {
      setLoading(false);
    }
  };

  const finishRegistration = () => {
    navigate(HOME_URL);
  };

  // ---------- helpers de render de planes ----------

  const getPlanUsersLabel = (plan: SuscriptionTypeDataType): string => {
    const max = Number(plan.maxUsers || 0);
    if (!max) return "Usuarios ilimitados";
    return max === 1 ? "1 usuario" : `Hasta ${max} usuarios`;
  };

  const getPlanLimitLabels = (plan: SuscriptionTypeDataType): string[] => {
    const limits = parseDocumentLimits(plan.documentLimits);
    if (!limits) return [];
    return DOCUMENT_LIMIT_CATALOG
      .map(node => ({ node, limit: getDocumentLimit(limits, node.id) }))
      .filter(({ limit }) => limit > 0)
      .map(({ node, limit }) => formatLimitLabel(node.id, limit));
  };

  const getPlanFeatureLabels = (plan: SuscriptionTypeDataType): string[] => {
    const features = parsePlanFeatures(plan.features);
    const hidden = parsePlanFeatures(plan.hiddenFeatures);
    return PLAN_FEATURE_CATALOG
      .filter(node => isFeatureAllowed(features, node.id) && hidden?.[node.id] !== true)
      .map(node => node.label)
      .slice(0, MAX_CARD_FEATURES);
  };

  // ---------- render por paso ----------

  const stepIndex = { account: 1, verify: 2, plan: 3, payment: 3 }[step];

  const renderSteps = (): ReactNode => (
    <div className={styles.steps}>
      {[
        { n: 1, label: "Cuenta" },
        { n: 2, label: "Verificación" },
        { n: 3, label: "Plan y pago" },
      ].map(s => (
        <div key={s.n} className={`${styles.stepDot} ${stepIndex >= s.n ? styles.stepDotActive : ""}`}>
          <span>{s.n}</span>
          <small>{s.label}</small>
        </div>
      ))}
    </div>
  );

  const renderAccount = (): ReactNode => (
    <Form onSubmit={submitAccount}>
      <h4 className={styles.stepTitle}>Cree su cuenta</h4>
      <FormGroup>
        <Label>Nombre o negocio</Label>
        <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Mi Empresa S.A." />
      </FormGroup>
      <FormGroup>
        <Label>Correo electrónico</Label>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@ejemplo.com" />
      </FormGroup>
      <Row>
        <Col xs="4">
          <FormGroup>
            <Label>Código país</Label>
            <Input value={telefonoCd} onChange={e => setTelefonoCd(e.target.value.replace(/\D/g, ""))} />
          </FormGroup>
        </Col>
        <Col xs="8">
          <FormGroup>
            <Label>Teléfono</Label>
            <Input value={telefono} onChange={e => setTelefono(e.target.value.replace(/\D/g, ""))} placeholder="88888888" />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label>Contraseña</Label>
        <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" />
      </FormGroup>
      <FormGroup>
        <Label>Confirmar contraseña</Label>
        <Input type="password" value={password2} onChange={e => setPassword2(e.target.value)} />
      </FormGroup>
      <FormGroup check className={styles.termsCheck}>
        <Input type="checkbox" id="acceptTerms" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} />
        <Label check for="acceptTerms">
          Acepto los{" "}
          <Link to="/auth/terms" target="_blank" rel="noopener noreferrer">términos de uso</Link>
        </Label>
      </FormGroup>
      <Button color="primary" type="submit" block disabled={loading}>Continuar</Button>
      <p className={styles.recaptchaNote}>
        Este sitio está protegido por reCAPTCHA.
      </p>
      <p className="text-center mt-3 mb-0">
        ¿Ya tiene cuenta? <Link to="/auth/login">Inicie sesión</Link>
      </p>
    </Form>
  );

  const renderVerify = (): ReactNode => (
    <Form onSubmit={submitCode}>
      <h4 className={styles.stepTitle}>Verifique su correo</h4>
      <p>Enviamos un código de verificación a <b>{email}</b>. Ingréselo para continuar.</p>
      <FormGroup>
        <Label>Código de verificación</Label>
        <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" inputMode="numeric" />
      </FormGroup>
      <Button color="primary" type="submit" block disabled={loading}>Verificar y continuar</Button>
      <Button color="link" type="button" block onClick={resendCode}>Reenviar código</Button>
    </Form>
  );

  const renderPlanCard = (plan: SuscriptionTypeDataType): ReactNode => {
    const isFree = !plan.onvoPriceId;
    const limitLabels = getPlanLimitLabels(plan);
    return (
      <Col xs="12" md="6" lg="4" key={plan.id} className="mb-3">
        <div className={styles.planCard}>
          <h5 className={styles.planName}>{plan.nombre}</h5>
          <div className={styles.planPrice}>
            {isFree ? "Gratis" : formatMoneyValue(Number(plan.monto || 0), getCurrencyNumericId(plan.currencyId))}
            {!isFree && <span className={styles.planPricePeriod}> / mes</span>}
          </div>
          <div className={styles.planUsers}>
            <FontAwesomeIcon icon={faUsers} /> {getPlanUsersLabel(plan)}
          </div>
          {limitLabels.length > 0 && (
            <div className={styles.planLimits}>
              <span className={styles.planLimitsTitle}>Documentos por mes</span>
              <ul>
                {limitLabels.map(l => (
                  <li key={l}><FontAwesomeIcon icon={faFileInvoice} className={styles.limitIcon} /> {l}</li>
                ))}
              </ul>
            </div>
          )}
          <ul className={styles.planFeatures}>
            {getPlanFeatureLabels(plan).map(l => (
              <li key={l}><FontAwesomeIcon icon={faCheck} className={styles.featureIcon} /> {l}</li>
            ))}
          </ul>
          <Button
            color={isFree ? "secondary" : "primary"}
            block
            disabled={loading}
            onClick={() => choosePlan(plan)}
          >
            {isFree ? "Comenzar gratis" : "Seleccionar"}
          </Button>
        </div>
      </Col>
    );
  };

  const renderPlan = (): ReactNode => (
    <>
      <h4 className={styles.stepTitle}>Elija su plan</h4>
      <p>Su cuenta ya está creada. Seleccione el plan que mejor se adapte a su negocio.</p>
      <Row>{plans.map(renderPlanCard)}</Row>
    </>
  );

  const renderPayment = (): ReactNode => (
    <>
      <h4 className={styles.stepTitle}>Complete su pago</h4>
      <Alert color="info" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>Está por suscribirse al plan <b>{selectedPlan?.nombre}</b>.</span>
        <Button color="link" className="p-0" onClick={() => { setStep("plan"); setSelectedPlan(null); setOnvoSession(null); }}>
          Cambiar plan
        </Button>
      </Alert>
      {onvoSession && (
        <OnvoSubscriptionPayment
          customerId={onvoSession.onvoCustomerId}
          subscriptionId={onvoSession.onvoSubscriptionId}
          onSuccess={onPaymentSuccess}
          onError={() => showAlert("danger", "No fue posible procesar el pago. Intente de nuevo.")}
        />
      )}
    </>
  );

  const isWideStep = step === "plan" || step === "payment";

  return (
    <Container className={styles.registerContainer}>
      <Card className={`${styles.registerCard} ${isWideStep ? styles.wide : ""}`}>
        <CardBody>
          {renderSteps()}
          {error && <Alert color="danger">{error}</Alert>}
          {step === "account" && renderAccount()}
          {step === "verify" && renderVerify()}
          {step === "plan" && renderPlan()}
          {step === "payment" && renderPayment()}
        </CardBody>
      </Card>
      <LoadingScreen isLoading={loading} />
    </Container>
  );
};

export default Register;
